import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useMessaging } from '../../contexts/MessagingContext';
import { supabase } from '../../utils/supabase';
import Icon from '../AppIcon';
import notificationSound from '../../utils/notificationSound';

/**
 * Floating chat bubble component that appears in the bottom right corner
 * Shows unread message count and provides quick access to messaging
 * Features real-time notifications via Supabase subscriptions
 * 
 * Key Features:
 * - Toggle mini chat with recent conversations
 * - Auto-scroll to latest messages
 * - Real-time message updates via Supabase subscriptions
 * - Loads 20 most recent messages for performance
 * - Automatic message read status updates
 * - Responsive toggle behavior between minimized and expanded states
 */
const FloatingChatBubble = () => {
  // Production debug flag - set to true only for debugging
  const DEBUG_MODE = process.env.NODE_ENV === 'development';
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { conversations, selectConversationById, loadConversations, sendMessage } = useMessaging();
  const [isMinimized, setIsMinimized] = useState(true);
  const [showTooltip, setShowTooltip] = useState(false);
  const [hasNewMessages, setHasNewMessages] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0); // Direct count from messages table
  const [showMiniChat, setShowMiniChat] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isMiniChatOpen, setIsMiniChatOpen] = useState(false); // Track if user explicitly opened mini chat
  const [isHidden, setIsHidden] = useState(false); // Track if user has closed the bubble
  const bubbleRef = useRef(null);
  const previousUnreadCount = useRef(0);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const messageInputRef = useRef(null);
  
  // Refs for throttling conversation refreshes
  const lastConversationRefresh = useRef(0);
  const conversationRefreshTimeout = useRef(null);
  const REFRESH_THROTTLE_MS = 3000; // 3 second minimum between refreshes

  // Don't show the bubble on the messaging page itself or any messages subpage
  const isOnMessagingPage = location.pathname.startsWith('/messages');

  // Persist and restore isHidden state from localStorage
  useEffect(() => {
    // Load hidden state from localStorage on mount
    const savedHiddenState = localStorage.getItem('floatingChatBubble_isHidden');
    if (savedHiddenState === 'true') {
      setIsHidden(true);
    }
  }, []);

  // Persist isHidden state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('floatingChatBubble_isHidden', isHidden.toString());
  }, [isHidden]);

  // Throttled conversation refresh to prevent repeated loads
  const throttledConversationRefresh = useCallback(() => {
    const now = Date.now();
    
    if (now - lastConversationRefresh.current < REFRESH_THROTTLE_MS) {
      // Schedule a delayed refresh if not already scheduled
      if (!conversationRefreshTimeout.current) {
        conversationRefreshTimeout.current = setTimeout(() => {
          lastConversationRefresh.current = Date.now();
          loadConversations();
          conversationRefreshTimeout.current = null;
        }, REFRESH_THROTTLE_MS);
      }
      return;
    }
    
    lastConversationRefresh.current = now;
    loadConversations();
  }, [loadConversations]);

  // Get recent conversations - prioritize unread, but show recent ones if no unread
  const recentUnreadConversations = useMemo(() => {
    // First, get unread conversations
    const unreadConversations = conversations
      .filter(conv => conv.unread_count > 0)
      .sort((a, b) => {
        const timeA = new Date(a.last_message_at || a.last_message?.timestamp || a.updated_at || 0);
        const timeB = new Date(b.last_message_at || b.last_message?.timestamp || b.updated_at || 0);
        return timeB - timeA;
      });
    
    // If we have unread conversations, use those
    if (unreadConversations.length > 0) {
      return unreadConversations.slice(0, 3);
    }
    
    // Otherwise, show the most recent conversations (even if all read)
    // This allows users to continue conversations from the chat bubble
    const recentConversations = conversations
      .sort((a, b) => {
        const timeA = new Date(a.last_message_at || a.last_message?.timestamp || a.updated_at || 0);
        const timeB = new Date(b.last_message_at || b.last_message?.timestamp || b.updated_at || 0);
        return timeB - timeA;
      })
      .slice(0, 3);
    
    return recentConversations;
  }, [conversations]);

  // Calculate total unread count from conversations as fallback
  const calculatedUnreadCount = useMemo(() => {
    return conversations.reduce((sum, conv) => sum + (conv.unread_count || 0), 0);
  }, [conversations]);

  // Use the maximum of database count or calculated count
  const displayUnreadCount = Math.max(unreadCount, calculatedUnreadCount);

  // Load conversations when user is available
  // IMPORTANT: Do NOT include loadConversations in dependencies to avoid infinite loops
  // loadConversations changes reference frequently, causing effect to re-run
  useEffect(() => {
    if (user?.id && !isOnMessagingPage) {
      loadConversations();
      // Note: Audio context initializes on first user interaction automatically
      // The notificationSound service handles this via the initOnInteraction listener
    }
  }, [user?.id, isOnMessagingPage]);

  // Manually enrich conversations with unread counts if not present
  // NOTE: This effect only runs once when component mounts to check initial state
  // It does NOT depend on loadConversations to avoid infinite loops
  useEffect(() => {
    const enrichConversationsWithUnreadCounts = async () => {
      if (!user?.id || isOnMessagingPage || conversations.length === 0) return;

      // Check if any conversation is missing unread_count
      const needsEnrichment = conversations.some(conv => 
        typeof conv.unread_count === 'undefined' || conv.unread_count === null
      );

      if (!needsEnrichment) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[FloatingChatBubble] ✅ All conversations have unread_count, no enrichment needed');
        }
        return; // All conversations already have unread counts
      }

      if (process.env.NODE_ENV === 'development') {
        console.log('[FloatingChatBubble] ⚠️ Conversations missing unread_count, fetching manually...');
      }

      try {
        // Fetch unread counts for all conversations
        const conversationIds = conversations.map(c => c.conversation_id || c.id);
        
        const { data, error } = await supabase
          .from('messages')
          .select('conversation_id')
          .in('conversation_id', conversationIds)
          .eq('recipient_id', user.id)
          .eq('is_read', false);

        if (error) {
          console.error('[FloatingChatBubble] Error fetching unread counts:', error);
          return;
        }

        // Count unread messages per conversation
        const unreadCounts = {};
        data?.forEach(msg => {
          unreadCounts[msg.conversation_id] = (unreadCounts[msg.conversation_id] || 0) + 1;
        });

        if (process.env.NODE_ENV === 'development') {
          console.log('[FloatingChatBubble] Manual unread counts:', unreadCounts);
        }

        // Update local unread count only - do not call loadConversations() to avoid infinite loop
        const manualCount = Object.values(unreadCounts).reduce((sum, count) => sum + count, 0);
        setUnreadCount(manualCount);
      } catch (error) {
        console.error('[FloatingChatBubble] Exception enriching conversations:', error);
      }
    };

    enrichConversationsWithUnreadCounts();
  }, [user?.id, isOnMessagingPage]); // Only depend on these - NOT conversations or loadConversations

  // Fetch initial unread message count directly from messages table
  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (!user?.id || isOnMessagingPage) return;

      try {
        const { count, error } = await supabase
          .from('messages')
          .select('*', { count: 'exact', head: true })
          .eq('recipient_id', user.id)
          .eq('is_read', false);

        if (error) {
          if (process.env.NODE_ENV === 'development') {
            console.error('[FloatingChatBubble] Error fetching initial unread count:', error);
          }
          return;
        }

        setUnreadCount(count || 0);
        previousUnreadCount.current = count || 0;
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('[FloatingChatBubble] Error fetching initial unread count:', error);
        }
      }
    };

    fetchUnreadCount();
  }, [user?.id, isOnMessagingPage]);

  // Debug conversations data structure (development only)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[FloatingChatBubble] State:', {
        conversationsCount: conversations.length,
        unreadCount: unreadCount,
        calculatedUnreadCount: calculatedUnreadCount,
        displayUnreadCount: displayUnreadCount,
        isOnMessagingPage: isOnMessagingPage,
        userId: user?.id,
        willShow: !(!user || isOnMessagingPage || displayUnreadCount === 0)
      });
    }
  }, [conversations.length, unreadCount, calculatedUnreadCount, displayUnreadCount, isOnMessagingPage, user?.id]);

  // Detect new messages and trigger animation + sound notification
  useEffect(() => {
    // Skip if on messaging page
    if (isOnMessagingPage) {
      previousUnreadCount.current = unreadCount;
      return;
    }
    
    // Only play sound if unread count increased
    if (unreadCount > previousUnreadCount.current && previousUnreadCount.current >= 0) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[FloatingChatBubble] Unread count increased:', previousUnreadCount.current, '->', unreadCount);
      }
      
      // Show bubble again if it was hidden
      if (isHidden) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[FloatingChatBubble] New message received, showing bubble again');
        }
        setIsHidden(false);
        // Also clear from localStorage so it stays visible
        localStorage.removeItem('floatingChatBubble_isHidden');
      }
      
      setHasNewMessages(true);
      
      // Initialize audio context first if needed (browser autoplay policy requires user interaction)
      if (!notificationSound.initialized) {
        if (process.env.NODE_ENV === 'development') {
          console.log('[FloatingChatBubble] Initializing audio context for notification sound');
        }
        notificationSound.initialize();
      }
      
      // Play notification sound for new messages
      // Add a small delay to ensure audio context is ready
      setTimeout(async () => {
        try {
          if (process.env.NODE_ENV === 'development') {
            console.log('[FloatingChatBubble] Playing notification sound...');
          }
          await notificationSound.playMessageNotification();
          if (process.env.NODE_ENV === 'development') {
            console.log('[FloatingChatBubble] Notification sound playback initiated');
          }
        } catch (err) {
          console.error('[FloatingChatBubble] Failed to play notification sound:', err);
        }
      }, 100);
      
      // Reset animation after 3 seconds
      const timer = setTimeout(() => setHasNewMessages(false), 3000);
      
      // IMPORTANT: Update previous count BEFORE returning cleanup
      previousUnreadCount.current = unreadCount;
      
      return () => clearTimeout(timer);
    }
    
    // Always update the previous count reference
    previousUnreadCount.current = unreadCount;
  }, [unreadCount, isOnMessagingPage, isHidden]);

  // Helper function to scroll to bottom with proper timing
  const scrollToBottom = useCallback(() => {
    if (!messagesContainerRef.current) return;
    
    if (process.env.NODE_ENV === 'development') {
      console.log('[FloatingChatBubble] Attempting to scroll to bottom', {
        containerExists: !!messagesContainerRef.current,
        scrollHeight: messagesContainerRef.current?.scrollHeight,
        clientHeight: messagesContainerRef.current?.clientHeight,
        currentScrollTop: messagesContainerRef.current?.scrollTop
      });
    }
    
    // Use a small delay to ensure DOM updates
    const scrollTimer = setTimeout(() => {
      if (messagesContainerRef.current) {
        const container = messagesContainerRef.current;
        container.scrollTop = container.scrollHeight;
        
        if (process.env.NODE_ENV === 'development') {
          console.log('[FloatingChatBubble] Scrolled to bottom - new scrollTop:', container.scrollTop);
        }
      }
    }, 0);
    
    return () => clearTimeout(scrollTimer);
  }, []);

  // Auto-scroll to bottom when messages load/change
  useEffect(() => {
    // Only scroll if mini chat is open AND we have messages
    if (showMiniChat && !isLoadingMessages && messages.length > 0) {
      // Small delay to ensure DOM has fully updated
      const scrollTimer = setTimeout(() => {
        scrollToBottom();
      }, 100);
      return () => clearTimeout(scrollTimer);
    }
  }, [messages.length, showMiniChat, isLoadingMessages, scrollToBottom]);

  // Auto-scroll when mini chat first opens
  useEffect(() => {
    if (showMiniChat && !isLoadingMessages) {
      // Slightly longer delay when first opening to let everything render
      const scrollTimer = setTimeout(() => {
        scrollToBottom();
      }, 150);
      return () => clearTimeout(scrollTimer);
    }
  }, [showMiniChat, scrollToBottom]);

  // Focus input when mini chat opens
  useEffect(() => {
    if (showMiniChat && messageInputRef.current) {
      messageInputRef.current.focus();
    }
  }, [showMiniChat]);

  // Load messages for selected conversation
  const loadConversationMessages = useCallback(async (conversationId) => {
    if (!conversationId) {
      console.warn('[FloatingChatBubble] loadConversationMessages called without conversationId');
      return;
    }

    setIsLoadingMessages(true);
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('[FloatingChatBubble] Loading messages for conversation:', conversationId);
      }

      // Match the query structure used in unifiedMessagingService
      const { data, error } = await supabase
        .from('messages')
        .select(`
          id, 
          sender_id, 
          recipient_id, 
          content, 
          sent_at, 
          message_type, 
          is_read, 
          attachments,
          sender:user_profiles!messages_sender_id_fkey(full_name, profile_picture)
        `)
        .eq('conversation_id', conversationId)
        .order('sent_at', { ascending: false }) // Load newest first
        .limit(20); // Get last 20 messages

      if (error) {
        console.error('[FloatingChatBubble] Error loading messages:', error.message);
        setIsLoadingMessages(false);
        return;
      }

      if (process.env.NODE_ENV === 'development') {
        console.log('[FloatingChatBubble] Loaded messages:', data?.length || 0, 'messages');
      }

      // Transform data to match expected format - REVERSE to show chronologically (oldest to newest)
      const formattedMessages = (data || []).reverse().map(msg => ({
        id: msg.id,
        sender_id: msg.sender_id,
        recipient_id: msg.recipient_id,
        content: msg.content,
        created_at: msg.sent_at, // Map sent_at to created_at for display
        sent_at: msg.sent_at,
        message_type: msg.message_type,
        is_read: msg.is_read,
        attachments: msg.attachments,
        sender_name: msg.sender?.full_name || 'Unknown',
        sender_avatar: msg.sender?.profile_picture
      }));

      setMessages(formattedMessages);
      setIsLoadingMessages(false);
    } catch (error) {
      console.error('[FloatingChatBubble] Exception loading messages:', error.message);
      setIsLoadingMessages(false);
    }
  }, []);

  // Subscribe to new messages in the mini chat
  useEffect(() => {
    // Check if mini chat is open AND conversation has an ID (either conversation_id or id)
    const conversationId = selectedConversation?.conversation_id || selectedConversation?.id;
    
    if (!showMiniChat || !conversationId) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[FloatingChatBubble] Not subscribing to messages:', {
          showMiniChat,
          conversationId: conversationId,
          selectedConversation: selectedConversation
        });
      }
      return;
    }
    
    if (process.env.NODE_ENV === 'development') {
      console.log('[FloatingChatBubble] ⚡ Setting up message subscription for conversation:', conversationId);
    }
    
    const channelName = `mini_chat_${conversationId}`;
    const channel = supabase
      .channel(channelName)
      .on('postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${conversationId}`
        },
        (payload) => {
          if (process.env.NODE_ENV === 'development') {
            console.log('[FloatingChatBubble] 📩 New message received in mini chat:', {
              messageId: payload.new?.id,
              content: payload.new?.content,
              sender: payload.new?.sender_id,
              conversationId: payload.new?.conversation_id
            });
          }
          // Transform the message to match our expected format
          const newMsg = payload.new;
          const formattedMsg = {
            id: newMsg.id,
            sender_id: newMsg.sender_id,
            recipient_id: newMsg.recipient_id,
            content: newMsg.content,
            created_at: newMsg.sent_at, // Map sent_at to created_at
            sent_at: newMsg.sent_at,
            message_type: newMsg.message_type,
            is_read: newMsg.is_read,
            attachments: newMsg.attachments
          };
          
          if (process.env.NODE_ENV === 'development') {
            console.log('[FloatingChatBubble] Adding formatted message to state:', formattedMsg);
          }
          
          setMessages(prev => {
            // Check if message already exists (to avoid duplicates from optimistic updates)
            const exists = prev.some(m => m.id === formattedMsg.id);
            if (exists) {
              if (process.env.NODE_ENV === 'development') {
                console.log('[FloatingChatBubble] Message already exists, skipping duplicate');
              }
              return prev;
            }
            
            // New message added - schedule scroll to bottom
            const newMessages = [...prev, formattedMsg];
            setTimeout(() => {
              scrollToBottom();
            }, 50);
            
            return newMessages;
          });
        }
      )
      .subscribe((status, err) => {
        if (process.env.NODE_ENV === 'development') {
          console.log(`[FloatingChatBubble] Mini chat subscription (${channelName}) status:`, status, err ? `Error: ${err}` : '');
          if (status === 'SUBSCRIBED') {
            console.log('[FloatingChatBubble] ✅ Mini chat subscription is ACTIVE and listening for messages');
          }
        }
      });

    return () => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[FloatingChatBubble] 🔌 Removing mini chat subscription:', channelName);
      }
      supabase.removeChannel(channel);
    };
  }, [showMiniChat, selectedConversation, scrollToBottom]);

  // Handle click outside to close expanded view (minimize but preserve state)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (bubbleRef.current && !bubbleRef.current.contains(event.target) && !isMinimized) {
        // Just minimize, don't clear mini chat state so user can resume
        setIsMinimized(true);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMinimized]);

  // Function to refresh unread count from database
  const refreshUnreadCount = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      const { count, error } = await supabase
        .from('messages')
        .select('*', { count: 'exact', head: true })
        .eq('recipient_id', user.id)
        .eq('is_read', false);

      if (!error && count !== null) {
        setUnreadCount(prevCount => {
          if (count !== prevCount) {
            if (process.env.NODE_ENV === 'development') {
              console.log('[FloatingChatBubble] Unread count refreshed:', prevCount, '->', count);
            }
          }
          return count;
        });
      }
    } catch (error) {
      console.error('[FloatingChatBubble] Error refreshing unread count:', error);
    }
  }, [user?.id]);

  // Auto-refresh unread count periodically as fallback for realtime
  useEffect(() => {
    if (!user?.id || isOnMessagingPage) return;

    // Refresh every 15 seconds (increased from 10) to reduce load
    const refreshInterval = setInterval(() => {
      refreshUnreadCount();
      throttledConversationRefresh();
    }, 15000);

    return () => {
      clearInterval(refreshInterval);
      // Cleanup throttle timeout on unmount
      if (conversationRefreshTimeout.current) {
        clearTimeout(conversationRefreshTimeout.current);
        conversationRefreshTimeout.current = null;
      }
    };
  }, [user?.id, isOnMessagingPage, throttledConversationRefresh, refreshUnreadCount]);

  // Real-time subscription for message changes (direct from messages table)
  useEffect(() => {
    if (!user?.id || isOnMessagingPage) return;

    let isSubscribed = true;
    
    if (process.env.NODE_ENV === 'development') {
      console.log('[FloatingChatBubble] Setting up realtime subscription for user:', user.id);
    }

    const messagesChannel = supabase
      .channel(`floating_bubble_messages_${user.id}`)
      .on('postgres_changes', 
        { 
          event: 'INSERT', 
          schema: 'public', 
          table: 'messages',
          filter: `recipient_id=eq.${user.id}`
        }, 
        (payload) => {
          if (!isSubscribed) return;
          
          const newMessage = payload.new;
          
          if (process.env.NODE_ENV === 'development') {
            console.log('[FloatingChatBubble] New message received:', newMessage?.id, 'is_read:', newMessage?.is_read);
          }
          
          if (newMessage && !newMessage.is_read) {
            // New unread message received - increment count
            setUnreadCount(prev => {
              const newCount = prev + 1;
              if (process.env.NODE_ENV === 'development') {
                console.log('[FloatingChatBubble] Unread count updated:', prev, '->', newCount);
              }
              return newCount;
            });
          }
          
          // IMPORTANT: Force fresh conversation load to get updated unread counts
          if (process.env.NODE_ENV === 'development') {
            console.log('[FloatingChatBubble] 🔄 Forcing fresh conversation load');
          }
          // Use a small delay to allow the message to be committed to the database
          setTimeout(() => {
            loadConversations(true); // Force fresh load bypassing cache
          }, 500);
        }
      )
      .on('postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'messages',
          filter: `recipient_id=eq.${user.id}`
        },
        (payload) => {
          if (!isSubscribed) return;
          
          const { old: oldRecord, new: newRecord } = payload;
          
          if (process.env.NODE_ENV === 'development') {
            console.log('[FloatingChatBubble] Message updated:', newRecord?.id, 'is_read:', oldRecord?.is_read, '->', newRecord?.is_read);
          }
          
          // If message was marked as read
          if (oldRecord?.is_read === false && newRecord?.is_read === true) {
            setUnreadCount(prev => Math.max(0, prev - 1));
            // Force fresh conversation load
            setTimeout(() => {
              loadConversations(true);
            }, 300);
          }
          // If message was marked as unread
          else if (oldRecord?.is_read === true && newRecord?.is_read === false) {
            setUnreadCount(prev => prev + 1);
            setTimeout(() => {
              loadConversations(true);
            }, 300);
          }
        }
      )
      .subscribe((status, err) => {
        if (process.env.NODE_ENV === 'development') {
          console.log('[FloatingChatBubble] Realtime subscription status:', status, err || '');
        }
        
        if (status === 'SUBSCRIBED') {
          if (process.env.NODE_ENV === 'development') {
            console.log('[FloatingChatBubble] ✅ Realtime subscription active');
          }
          // Refresh unread count when subscription is established to catch any missed messages
          refreshUnreadCount();
        } else if (status === 'CHANNEL_ERROR') {
          console.error('[FloatingChatBubble] ❌ Realtime subscription error:', err);
        } else if (status === 'TIMED_OUT') {
          console.error('[FloatingChatBubble] ❌ Realtime subscription timed out');
        }
      });

    return () => {
      isSubscribed = false;
      if (process.env.NODE_ENV === 'development') {
        console.log('[FloatingChatBubble] Cleaning up realtime subscription');
      }
      messagesChannel.unsubscribe();
      supabase.removeChannel(messagesChannel);
    };
  }, [user?.id, isOnMessagingPage, loadConversations, refreshUnreadCount]);

  // Don't render if user is not logged in or on messaging page
  // Show bubble if:
  // - There are unread messages
  // - Mini chat is currently open (even if messages are read)
  // - There are conversations to show (for continuing conversations)
  // - User hasn't hidden it
  const shouldShowBubble = displayUnreadCount > 0 || isMiniChatOpen || showMiniChat || (recentUnreadConversations.length > 0);
  
  if (!user || isOnMessagingPage || !shouldShowBubble || isHidden) {
    if (process.env.NODE_ENV === 'development' && user && !isOnMessagingPage) {
      console.log('[FloatingChatBubble] Not showing bubble:', {
        hasUser: !!user,
        isOnMessagingPage,
        displayUnreadCount,
        isMiniChatOpen,
        showMiniChat,
        recentConversationsCount: recentUnreadConversations.length,
        shouldShowBubble
      });
    }
    return null;
  }

  const handleBubbleClick = async () => {
    // Toggle the mini chat
    if (showMiniChat) {
      // Close mini chat
      handleCloseMiniChat();
    } else {
      // Open mini chat
      if (process.env.NODE_ENV === 'development') {
        console.log('[FloatingChatBubble] Opening mini chat from bubble click');
      }
      
      // If there are conversations, select the most recent one
      if (recentUnreadConversations.length > 0) {
        try {
          const mostRecentConv = recentUnreadConversations[0];
          await handleOpenMiniChat(mostRecentConv);
        } catch (error) {
          if (process.env.NODE_ENV === 'development') {
            console.error('[FloatingChatBubble] Error opening mini chat:', error);
          }
        }
      }
      
      // Expand the preview if minimized
      if (isMinimized) {
        setIsMinimized(false);
      }
    }
  };

  const handleCloseBubble = (e) => {
    e.stopPropagation();
    if (process.env.NODE_ENV === 'development') {
      console.log('[FloatingChatBubble] Closing chat bubble');
    }
    setIsHidden(true);
    // Clean up state
    setShowMiniChat(false);
    setSelectedConversation(null);
    setMessages([]);
    setMessageInput('');
    setIsMinimized(true);
    setIsMiniChatOpen(false);
  };

  const handleClosePreview = (e) => {
    e.stopPropagation();
    if (process.env.NODE_ENV === 'development') {
      console.log('[FloatingChatBubble] Closing preview');
    }
    // Just minimize the expanded preview, don't hide the bubble
    setIsMinimized(true);
  };

  const handleOpenMiniChat = async (conv) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[FloatingChatBubble] Opening mini chat for conversation:', {
        conv_full: conv,
        conversationId: conv.conversation_id || conv.id,
        participantName: conv.participant_name,
        unreadCount: conv.unread_count,
        participant_1: conv.participant_1,
        participant_2: conv.participant_2
      });
    }

    // Store the conversation with its ID properly set
    const conversationId = conv.conversation_id || conv.id;
    const conversationWithId = {
      ...conv,
      id: conversationId, // Ensure id field exists
      conversation_id: conversationId // Ensure conversation_id field exists
    };

    setSelectedConversation(conversationWithId);
    setShowMiniChat(true);
    setIsMiniChatOpen(true); // Mark that user explicitly opened mini chat
    
    if (process.env.NODE_ENV === 'development') {
      console.log('[FloatingChatBubble] Loading messages with conversationId:', conversationId);
    }
    
    await loadConversationMessages(conversationId);
    
    // Mark messages as read
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('[FloatingChatBubble] Marking messages as read for conversation:', conversationId);
      }

      const { error } = await supabase
        .from('messages')
        .update({ is_read: true })
        .eq('conversation_id', conversationId)
        .eq('recipient_id', user.id)
        .eq('is_read', false);

      if (error) {
        console.error('[FloatingChatBubble] Error marking messages as read:', error);
      } else if (process.env.NODE_ENV === 'development') {
        console.log('[FloatingChatBubble] ✅ Messages marked as read');
      }
    } catch (error) {
      console.error('[FloatingChatBubble] Exception marking messages as read:', error);
    }
  };

  const handleBackToList = () => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[FloatingChatBubble] Going back to conversation list');
    }
    setShowMiniChat(false);
    setSelectedConversation(null);
    setMessages([]);
    setMessageInput('');
  };

  const handleCloseMiniChat = () => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[FloatingChatBubble] Closing mini chat');
    }
    setShowMiniChat(false);
    setSelectedConversation(null);
    setMessages([]);
    setMessageInput('');
    setIsMiniChatOpen(false); // Mark that mini chat was closed by user
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!messageInput.trim() || isSending || !selectedConversation) return;

    setIsSending(true);
    const messageContent = messageInput.trim();
    const conversationId = selectedConversation.conversation_id || selectedConversation.id;
    
    // Derive recipient_id from participants (the one who is NOT the current user)
    const recipientId = selectedConversation.participant_1 === user.id 
      ? selectedConversation.participant_2 
      : selectedConversation.participant_1;

    if (process.env.NODE_ENV === 'development') {
      console.log('[FloatingChatBubble] Sending message:', {
        conversationId,
        recipientId,
        senderId: user.id,
        content: messageContent
      });
    }

    // Optimistic update - add message to UI immediately
    const optimisticMessage = {
      id: `temp-${Date.now()}`, // Temporary ID
      sender_id: user.id,
      recipient_id: recipientId,
      content: messageContent,
      created_at: new Date().toISOString(),
      sent_at: new Date().toISOString(),
      message_type: 'text',
      is_read: false,
      attachments: null,
      isOptimistic: true // Flag to identify this as optimistic
    };

    if (process.env.NODE_ENV === 'development') {
      console.log('[FloatingChatBubble] Adding optimistic message:', optimisticMessage);
    }

    setMessages(prev => [...prev, optimisticMessage]);
    setMessageInput('');

    // Scroll to show the optimistic message
    setTimeout(() => {
      scrollToBottom();
    }, 50);

    try {
      const result = await sendMessage(conversationId, messageContent, recipientId);
      
      if (process.env.NODE_ENV === 'development') {
        console.log('[FloatingChatBubble] Message sent successfully:', result);
      }

      // Note: The realtime subscription will handle adding the actual message
      // Remove the optimistic message to avoid duplicates
      setMessages(prev => prev.filter(msg => msg.id !== optimisticMessage.id));
      
    } catch (error) {
      console.error('[FloatingChatBubble] Error sending message:', error);
      // Remove the optimistic message on error
      setMessages(prev => prev.filter(msg => msg.id !== optimisticMessage.id));
      // Restore the message input
      setMessageInput(messageContent);
      // Show error to user (you can add a toast notification here)
      alert('Failed to send message. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  const formatMessageTime = (timestamp) => {
    if (!timestamp) return '';
    
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    } else if (diffInHours < 48) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  return (
    <div
      ref={bubbleRef}
      className="hidden md:flex fixed z-50 transition-all duration-300 ease-in-out flex-col items-end"
      style={{
        bottom: '20px',
        right: '20px',
      }}
    >
      {/* Expanded Preview (Desktop only) */}
      {!isMinimized && (
        <div className="hidden md:block mb-4 bg-white rounded-lg shadow-2xl border border-gray-200 w-80 max-h-[600px] overflow-hidden animate-slideUp">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              {showMiniChat && selectedConversation ? (
                <>
                  <button
                    onClick={handleBackToList}
                    className="text-white hover:text-gray-200 transition-colors mr-1"
                  >
                    <Icon name="ArrowLeft" size={18} />
                  </button>
                  <div className="flex items-center space-x-2">
                    {selectedConversation.participant_avatar ? (
                      <img
                        src={selectedConversation.participant_avatar}
                        alt={selectedConversation.participant_name}
                        className="w-8 h-8 rounded-full object-cover border-2 border-white"
                      />
                    ) : (
                      <div className="w-8 h-8 bg-blue-400 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                        {selectedConversation.participant_name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                    )}
                    <h3 className="font-semibold text-sm truncate max-w-[160px]">
                      {selectedConversation.participant_name}
                    </h3>
                  </div>
                </>
              ) : (
                <>
                  <Icon name="MessageCircle" size={20} />
                  <h3 className="font-semibold">Messages</h3>
                </>
              )}
            </div>
            <button
              onClick={handleClosePreview}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <Icon name="X" size={18} />
            </button>
          </div>

          {/* Mini Chat View */}
          {showMiniChat && selectedConversation ? (
            <div className="flex flex-col h-[500px] z-10 bg-white border-t border-gray-200">
              {/* Messages Area */}
              <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
                {isLoadingMessages ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : messages.length > 0 ? (
                  <>
                    {messages.map((msg) => {
                      const isOwnMessage = msg.sender_id === user.id;
                      const messageTime = msg.sent_at || msg.created_at;
                      return (
                        <div
                          key={msg.id}
                          className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-[75%] rounded-lg px-3 py-2 ${
                              isOwnMessage
                                ? 'bg-blue-600 text-white'
                                : 'bg-white text-gray-900 border border-gray-200'
                            }`}
                          >
                            <p className="text-sm break-words">{msg.content}</p>
                            <p
                              className={`text-xs mt-1 ${
                                isOwnMessage ? 'text-blue-100' : 'text-gray-500'
                              }`}
                            >
                              {formatMessageTime(messageTime)}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </>
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-500">
                    <div className="text-center">
                      <Icon name="MessageSquare" size={32} className="mx-auto mb-2 text-gray-300" />
                      <p className="text-sm">No messages yet</p>
                      {process.env.NODE_ENV === 'development' && selectedConversation && (
                        <p className="text-xs text-gray-400 mt-2">
                          Conv ID: {selectedConversation.conversation_id || selectedConversation.id}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Message Input */}
              <form onSubmit={handleSendMessage} className="p-3 bg-white border-t border-gray-200">
                <div className="flex items-center space-x-2">
                  <input
                    ref={messageInputRef}
                    type="text"
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    placeholder="Type a message..."
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    disabled={isSending}
                  />
                  <button
                    type="submit"
                    disabled={!messageInput.trim() || isSending}
                    className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white p-2 rounded-lg transition-colors"
                  >
                    {isSending ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    ) : (
                      <Icon name="Send" size={18} />
                    )}
                  </button>
                </div>
              </form>
            </div>
          ) : (
            /* Conversations List */
            <div className="max-h-[500px] overflow-y-auto">
              {recentUnreadConversations.length > 0 ? (
                <div className="divide-y divide-gray-100">
                  {recentUnreadConversations.map((conv) => (
                    <div
                      key={conv.conversation_id || conv.id}
                      className="p-3 hover:bg-gray-50 cursor-pointer transition-colors group"
                    >
                      <div className="flex items-start space-x-3">
                        {/* Avatar */}
                        <div className="flex-shrink-0">
                          {conv.participant_avatar ? (
                            <img
                              src={conv.participant_avatar}
                              alt={conv.participant_name}
                              className="w-10 h-10 rounded-full object-cover border-2 border-white shadow-sm"
                            />
                          ) : (
                            <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold">
                              {conv.participant_name?.charAt(0)?.toUpperCase() || '?'}
                            </div>
                          )}
                        </div>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <h4 className="font-semibold text-sm text-gray-900 truncate">
                              {conv.participant_name}
                            </h4>
                            {conv.unread_count > 0 && (
                              <span className="bg-blue-500 text-white text-xs font-bold px-2 py-0.5 rounded-full ml-2">
                                {conv.unread_count}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-600 line-clamp-2 mb-2">
                            {typeof conv.last_message === 'string' ? conv.last_message : conv.last_message?.content || 'No messages yet'}
                          </p>
                          {/* Action Buttons */}
                          <div className="flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenMiniChat(conv);
                              }}
                              className="text-xs bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded transition-colors"
                            >
                              Reply
                            </button>
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                const success = await selectConversationById(conv.conversation_id || conv.id);
                                if (success) {
                                  handleCloseMiniChat();
                                  setIsMinimized(true);
                                  navigate('/messages');
                                }
                              }}
                              className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1 rounded transition-colors"
                            >
                              View Full
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-6 text-center text-gray-500">
                  <Icon name="MessageSquare" size={32} className="mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">No new messages</p>
                </div>
              )}
            </div>
          )}

          {/* Footer - Only show when not in mini chat */}
          {!showMiniChat && (
            <div className="border-t border-gray-200 p-3">
              <button
                onClick={() => {
                  handleCloseMiniChat();
                  navigate('/messages');
                }}
                className="w-full text-white text-sm font-medium py-2 rounded-lg transition-opacity hover:opacity-90"
                style={{ background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' }}
              >
                View All Messages
              </button>
            </div>
          )}
        </div>
      )}

      {/* Floating Bubble Button */}
      {isMinimized && (
        <div className="fixed bottom-24 right-6">
          <button
            onClick={handleBubbleClick}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            className={`
              w-14 h-14 md:w-16 md:h-16
              bg-gradient-to-br from-blue-600 to-blue-700
              hover:from-blue-700 hover:to-blue-800
              active:from-blue-800 active:to-blue-900
              text-white
              rounded-full
              shadow-lg hover:shadow-xl
              transition-all duration-300
              flex items-center justify-center
              transform hover:scale-110 active:scale-95
              ${hasNewMessages ? 'animate-bounce' : ''}
            `}
            aria-label="Open messages"
          >
            <Icon name="MessageCircle" size={24} className="md:w-7 md:h-7" />
            
            {/* Unread Count Badge */}
            {displayUnreadCount > 0 && (
              <span
                className={`
                  absolute -top-1 -right-1
                  bg-red-500
                  text-white
                  text-xs font-bold
                  min-w-[20px] h-5
                  px-1.5
                  rounded-full
                  flex items-center justify-center
                  shadow-md
                  ${hasNewMessages ? 'animate-pulse' : ''}
                `}
              >
                {displayUnreadCount > 99 ? '99+' : displayUnreadCount}
              </span>
            )}

            {/* Pulse animation for new messages */}
            {hasNewMessages && (
              <span className="absolute inset-0 rounded-full bg-blue-400 animate-ping opacity-75" />
            )}
          </button>

          {/* Tooltip (Desktop only) */}
          {showTooltip && isMinimized && (
            <div className="hidden md:block absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg whitespace-nowrap shadow-lg animate-fadeIn">
              {displayUnreadCount > 0 ? `${displayUnreadCount} unread message${displayUnreadCount > 1 ? 's' : ''}` : 'Messages'}
              <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900" />
            </div>
          )}

          {/* Close Button (Desktop only) */}
          <button
            onClick={handleCloseBubble}
            className="hidden md:flex absolute -top-2 -left-2 w-6 h-6 bg-white hover:bg-gray-100 text-gray-700 rounded-full shadow-md items-center justify-center transition-all duration-200 hover:scale-110"
            aria-label="Close chat bubble"
          >
            <Icon name="X" size={14} />
          </button>
        </div>
      )}
    </div>
  );
};

export default FloatingChatBubble;
